import { AnimatePresence, motion } from "motion/react";
import { Suspense, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate } from "react-router";
import { twJoin } from "tailwind-merge";

import { CardStack, CardTab, CardTabs } from "@/components/Card";
import { ContentFull, ContentLayout } from "@/components/ContentLayout";
import { FullScreenLayout } from "@/components/FullScreenLayout";

import { AddFriendModal } from "./components/AddFriendModal";
import { FriendGroupCard } from "./components/FriendGroupCard";
import { FriendGroups } from "./components/FriendGroups";
import { Friends } from "./components/Friends";
import { ReceivedFriendRequests } from "./components/ReceivedFriendRequests";

type Tab = "friends" | "groups";

function sanitizeTab(value: string | null): Tab | null {
  if (value === "friends" || value === "groups") return value;
  return "friends";
}

function sanitizeGroup(value: string | null): number | null {
  if (value === null) return null;
  const parsed = Number.parseInt(value, 10);
  return Number.isNaN(parsed) ? null : parsed;
}

export function FriendsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const location = useLocation();
  const search = new URLSearchParams(location.search);
  const selectedFriendGroupId = sanitizeGroup(search.get("group"));
  const activeTab = sanitizeTab(selectedFriendGroupId === null ? search.get("tab") : "groups");

  const [addFriendDialog, setAddFriendDialog] = useState<HTMLDialogElement | null>(null);

  return (
    <FullScreenLayout>
      <ContentLayout wet title={<Link to="/friends">{t("friends")}</Link>}>
        <ContentFull className="pt-26">
          <CardStack>
            <CardTabs className="sm:hidden">
              <CardTab active={activeTab === "friends"} onClick={() => navigate({ search: "?tab=friends" })}>
                {t("friends")}
              </CardTab>
              <CardTab active={activeTab === "groups"} onClick={() => navigate({ search: "?tab=groups" })}>
                {t("friend-groups")}
              </CardTab>
            </CardTabs>

            <div className="flex flex-row gap-4">
              <div
                className={twJoin(
                  "relative grow flex-col",
                  activeTab === "friends" && "hidden sm:flex",
                  activeTab === "groups" && "flex",
                )}
              >
                <AnimatePresence initial={false}>
                  {selectedFriendGroupId === null && (
                    <motion.div
                      key="A"
                      className="flex w-full flex-col"
                      initial={{ position: "absolute", transform: "translateX(-1rem)", opacity: 0 }}
                      animate={{ position: "static", transform: "translateX(0)", opacity: 1 }}
                      exit={{ position: "absolute", transform: "translateX(-1rem)", opacity: 0 }}
                      transition={{ duration: 0.15 }}
                    >
                      <FriendGroups onSelectFriendGroup={(group) => navigate({ search: `?group=${group.id}` })} />
                    </motion.div>
                  )}

                  {selectedFriendGroupId !== null && (
                    <motion.div
                      key="B"
                      className="flex w-full flex-col"
                      initial={{ position: "absolute", transform: "translateX(1rem)", opacity: 0 }}
                      animate={{ position: "static", transform: "translateX(0)", opacity: 1 }}
                      exit={{ position: "absolute", transform: "translateX(1rem)", opacity: 0 }}
                      transition={{ duration: 0.15 }}
                    >
                      <FriendGroupCard
                        onBack={() => navigate({ search: "?tab=groups" })}
                        friendGroupId={selectedFriendGroupId}
                      />
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
              <div
                className={twJoin(
                  "w-full shrink-0 flex-col gap-4 sm:w-100",
                  activeTab === "groups" && "hidden sm:flex",
                  activeTab === "friends" && "flex",
                )}
              >
                <Suspense>
                  <ReceivedFriendRequests />
                </Suspense>
                <Friends onAddFriend={() => addFriendDialog?.showModal()} />
              </div>
            </div>
          </CardStack>
        </ContentFull>
      </ContentLayout>

      <AddFriendModal ref={setAddFriendDialog} onClose={() => addFriendDialog?.close()} />
    </FullScreenLayout>
  );
}
